import { useState, useEffect, useCallback, useRef } from "react";
import { crApi, crEsPersonas } from "../lib/crScope";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Save, AlertTriangle, FileText, User, Building2, MinusCircle, Search, X,
  Upload, Loader2, Sparkles, FileUp, MessageSquare
} from "lucide-react";
import { ClienteAtencionWatcher } from "../contexts/AtencionPrioritariaContext";

// Estado del checklist de cotizaciones para un item (match por id o por texto)
const cotEstadoFor = (maps, id, texto) =>
  maps ? (maps.byId[id] ?? maps.byTexto[(texto || "").toLowerCase().trim()] ?? null) : null;

const COT_CHIP = {
  si: ["SI", "bg-green-100 text-green-700 border-green-300"],
  no: ["NO", "bg-red-100 text-red-700 border-red-300"],
  no_aplica: ["N/A", "bg-slate-200 text-slate-600 border-slate-300"],
};

export default function ChecklistModal({ 
  open, 
  onClose, 
  renovacion, 
  API_URL, 
  getAuthHeaders,
  onSaved,
  currentUser,  // Add currentUser prop
  origen  // undefined (renovaciones) | "cotizaciones"
}) {
  const [plantilla, setPlantilla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noTemplate, setNoTemplate] = useState(false);
  
  // Form data
  const [realizadoPor, setRealizadoPor] = useState("");
  const [supervisadoPor, setSupervisadoPor] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [itemsRespuesta, setItemsRespuesta] = useState([]);
  const [searchCobertura, setSearchCobertura] = useState("");
  
  // AI Document Analysis
  const [analyzing, setAnalyzing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  
  // Tipo de cobertura (for VEHICULO DE MOTOR and FIDELIDAD 3D)
  const [tipoCobertura, setTipoCobertura] = useState("");
  const [needsTipoCobertura, setNeedsTipoCobertura] = useState(false);
  const [tipoCoberturaOpciones, setTipoCoberturaOpciones] = useState([]);
  const [tipoCoberturaLabel, setTipoCoberturaLabel] = useState("Tipo de Cobertura");

  // Comparación vs checklist de cotizaciones (registros transferidos desde Cotizaciones/Emisiones)
  const [cotChecklist, setCotChecklist] = useState(null); // { byId, byTexto, fecha, realizado_por }

  // Timer (visible solo a admin/supervisor). Inicia cuando el formulario queda
  // listo para llenar: (a) plantilla cargada sin tipo_cobertura, o (b) usuario
  // selecciona el tipo_cobertura.
  const [startedAt, setStartedAt] = useState(null);  // Date object
  const [elapsedSec, setElapsedSec] = useState(0);

  // Trazabilidad de auditoría: eventos capturados durante la sesión
  const eventosRef = useRef([]);
  const logEvento = (accion, detalle) => {
    eventosRef.current.push({ accion, detalle, timestamp: new Date().toISOString() });
  };

  // Prompt para crear ticket en Mesa de Ayuda cuando cobertura < 100%
  const [ticketPrompt, setTicketPrompt] = useState(null); // { id, porcentaje }
  const [creandoTicket, setCreandoTicket] = useState(false);

  const userRole = ((currentUser?.role || currentUser?.rol || "")).toLowerCase();
  const isAdminOrSupervisor =
    userRole === "admin" || userRole === "administrador" || userRole === "superadmin"
    || userRole.includes("supervisor")
    || currentUser?.is_admin === true;

  // Tick every second while we have a startedAt and modal is open
  useEffect(() => {
    if (!open || !startedAt) {
      setElapsedSec(0);
      return undefined;
    }
    const update = () => setElapsedSec(Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000)));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [open, startedAt]);

  const formatElapsed = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}h ${String(m).padStart(2, "0")}m ${String(sec).padStart(2, "0")}s`
      : `${m}m ${String(sec).padStart(2, "0")}s`;
  };

  // Auto-fill user data when modal opens
  useEffect(() => {
    if (open && currentUser) {
      // Auto-completar con el nombre del usuario logueado
      // Limpiar el nombre de caracteres extra y espacios
      const userName = (currentUser.name || currentUser.nombre || "").trim();
      setRealizadoPor(userName);
      
      // Auto-completar con el nombre del supervisor (si existe)
      // Note: Backend returns 'supervisor_nombre', not 'supervisor_name'
      const supervisorName = (currentUser.supervisor_nombre || currentUser.supervisor_name || currentUser.supervisor || "").trim();
      setSupervisadoPor(supervisorName);
      
      // Log para debugging
      console.log("ChecklistModal - Usuario actual:", {
        name: currentUser.name,
        nombre: currentUser.nombre,
        supervisor_nombre: currentUser.supervisor_nombre,
        supervisor_name: currentUser.supervisor_name,
        supervisor: currentUser.supervisor,
        realizadoPor: userName,
        supervisadoPor: supervisorName
      });
    }
  }, [open, currentUser]);

  // Load plantilla when modal opens
  useEffect(() => {
    if (open && renovacion) {
      eventosRef.current = [];
      logEvento("inicio", `El usuario inició la generación del checklist para ${renovacion.cliente || "el cliente"} (Póliza ${renovacion.poliza || "S/P"}).`);
      loadPlantilla();
    }
  }, [open, renovacion]);

  const loadPlantilla = async () => {
    if (!renovacion?.ramo) {
      setNoTemplate(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setNoTemplate(false);
    setTipoCobertura("");  // Reset tipo cobertura
    setTipoCoberturaOpciones([]);
    setTipoCoberturaLabel("Tipo de Cobertura");

    try {
      // First check if a checklist already exists for this renovation
      const existingRes = await fetch(
        `${API_URL}${crApi()}/checklist-renovaciones/por-renovacion/${renovacion.id}`,
        getAuthHeaders()
      );
      const existingData = await existingRes.json();

      // Checklist de la cotización de origen (si este registro fue transferido desde Cotizaciones)
      let cotMaps = null;
      if ((origen || "") !== "cotizaciones") {
        try {
          const rc = await fetch(
            `${API_URL}${crApi()}/checklist-renovaciones/checklist-cotizacion/${renovacion.id}`,
            getAuthHeaders()
          );
          if (rc.ok) {
            const dc = await rc.json();
            if (dc.exists && dc.checklist?.items?.length) {
              const byId = {}, byTexto = {};
              dc.checklist.items.forEach((ci) => {
                if (ci.item_id) byId[ci.item_id] = ci.estado;
                byTexto[(ci.texto || "").toLowerCase().trim()] = ci.estado;
              });
              cotMaps = { byId, byTexto, fecha: dc.checklist.fecha, realizado_por: dc.checklist.realizado_por };
              logEvento("comparacion_cotizaciones", `Se cargó el checklist de cotizaciones (${dc.checklist.fecha || "s/f"}, realizado por ${dc.checklist.realizado_por || "N/D"}) para comparación.`);
            }
          }
        } catch {}
      }
      setCotChecklist(cotMaps);

      const res = await fetch(
        `${API_URL}${crApi()}/checklist-renovaciones/plantillas/por-ramo/${encodeURIComponent(renovacion.ramo)}`, 
        getAuthHeaders()
      );
      
      const data = await res.json();
      
      if (data && data.id) {
        setPlantilla(data);
        
        // Check if this template needs tipo_cobertura selection
        const hasTipoCobertura = data.tiene_tipo_cobertura || 
          (data.items?.some(item => item.tipo_cobertura));
        setNeedsTipoCobertura(hasTipoCobertura);
        
        // Set opciones based on plantilla data or ramo
        if (hasTipoCobertura) {
          if (data.tipo_cobertura_opciones && data.tipo_cobertura_opciones.length > 0) {
            setTipoCoberturaOpciones(data.tipo_cobertura_opciones);
          } else {
            const ramoUpper = (renovacion.ramo || "").toUpperCase();
            if (ramoUpper.includes("VEHICULO") && ramoUpper.includes("MOTOR")) {
              setTipoCoberturaOpciones(["Daños Propios", "Daños a Tercero"]);
            } else if (ramoUpper.includes("FIDELIDAD") && ramoUpper.includes("3D")) {
              setTipoCoberturaOpciones(["Formato Americano", "Formato Inglés"]);
            }
          }
          
          const ramoUpper = (renovacion.ramo || "").toUpperCase();
          if (data.formatos_personalizados || (ramoUpper.includes("FIDELIDAD") && ramoUpper.includes("3D"))) {
            setTipoCoberturaLabel("Formato");
          } else {
            setTipoCoberturaLabel("Tipo de Cobertura");
          }
        }
        
        // If an existing checklist was found, load its saved answers
        if (existingData?.exists && existingData.checklist?.items?.length > 0) {
          const savedItems = existingData.checklist.items;
          const savedObs = existingData.checklist.observaciones_generales || "";
          const savedTipo = existingData.checklist.tipo_cobertura_seleccionado || "";
          
          if (savedTipo && hasTipoCobertura) {
            setTipoCobertura(savedTipo);
          }
          
          // Map saved items back to the UI format
          const plantillaObsMap = {};
          (data.items || []).forEach(pi => { plantillaObsMap[pi.id] = pi.observacion || null; });
          setItemsRespuesta(savedItems.map(si => ({
            item_id: si.item_id,
            texto: si.texto,
            categoria: si.categoria || null,
            tipo_cobertura: si.tipo_cobertura || null,
            observacion: plantillaObsMap[si.item_id] || null,
            estado: si.no_aplica ? "no_aplica" : si.tiene_cobertura ? "si" : "pendiente",
            comentario_ejecutivo: si.comentario_ejecutivo || "",
            comentario_supervisor: si.comentario_supervisor || "",
            cotizacion_estado: si.cotizacion_estado || cotEstadoFor(cotMaps, si.item_id, si.texto),
            diferencia: si.diferencia || "",
            monto_asegurado: si.monto_asegurado || "",
            ajuste_solicitado: si.ajuste_solicitado || ""
          })));
          setObservaciones(savedObs);
          toast.info("Checklist existente cargado para edición");
          // Re-edit: reiniciamos el cronómetro para medir solo este episodio.
          setStartedAt(new Date());
        } else if (!hasTipoCobertura) {
          initializeItems(data.items, null, cotMaps);
          // Plantilla simple sin tipo_cobertura: arrancamos el reloj ya.
          setStartedAt(new Date());
        }
      } else {
        setNoTemplate(true);
      }
    } catch (error) {
      console.error("Error loading plantilla:", error);
      setNoTemplate(true);
    } finally {
      setLoading(false);
    }
  };

  // Initialize items based on tipo_cobertura filter
  const initializeItems = (items, filterTipo = null, cotMaps = cotChecklist) => {
    let filteredItems = items || [];
    
    // Filter by tipo_cobertura if specified
    if (filterTipo) {
      filteredItems = items.filter(item => 
        item.tipo_cobertura === filterTipo || !item.tipo_cobertura
      );
    }
    
    setItemsRespuesta(filteredItems.map(item => ({
      item_id: item.id,
      texto: item.texto,
      categoria: item.categoria,
      tipo_cobertura: item.tipo_cobertura,
      observacion: item.observacion || null,
      estado: "pendiente", // "pendiente", "si", "no_aplica"
      comentario_ejecutivo: "",
      comentario_supervisor: "",
      cotizacion_estado: cotEstadoFor(cotMaps, item.id, item.texto),
      diferencia: "",
      monto_asegurado: "",
      ajuste_solicitado: ""
    })));
  };

  // Handle tipo_cobertura selection
  const handleTipoCoberturaChange = (value) => {
    setTipoCobertura(value);
    if (plantilla?.items) {
      initializeItems(plantilla.items, value);
    }
    // Iniciar cronómetro al elegir el tipo (solo la primera vez).
    setStartedAt((prev) => prev || new Date());
  };

  // Handle item state change (SI / N/A)
  const handleItemEstadoChange = (itemId, nuevoEstado) => {
    setItemsRespuesta(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, estado: nuevoEstado }
        : item
    ));
  };

  const handleComentarioEjecutivo = (itemId, value) => {
    setItemsRespuesta(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, comentario_ejecutivo: value }
        : item
    ));
  };

  const handleComentarioSupervisor = (itemId, value) => {
    setItemsRespuesta(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, comentario_supervisor: value }
        : item
    ));
  };

  // Campo genérico por item: diferencia | monto_asegurado | ajuste_solicitado
  const handleItemField = (itemId, field, value) => {
    setItemsRespuesta(prev => prev.map(item =>
      item.item_id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Si el valor es puramente numérico, lo formatea con separador de miles (ej: 1000000 -> 1,000,000)
  const formatNumericValue = (v) => {
    const s = String(v ?? "").trim();
    if (!s) return s;
    const raw = s.replace(/,/g, "");
    if (!/^\d+(\.\d+)?$/.test(raw)) return s;
    const [ent, dec] = raw.split(".");
    const entF = Number(ent).toLocaleString("en-US");
    return dec !== undefined ? `${entF}.${dec}` : entF;
  };

  // Comparación vs cotizaciones y columnas de montos (solo fuera del módulo de cotizaciones)
  const comparisonMode = !!cotChecklist && (origen || "") !== "cotizaciones";
  const showMontos = !comparisonMode && (origen || "") !== "cotizaciones";
  const isDiff = (item) => {
    if (!item.cotizacion_estado) return false;
    const rg = item.estado === "pendiente" ? "no" : item.estado;
    return item.cotizacion_estado !== rg;
  };
  const diffCount = comparisonMode ? itemsRespuesta.filter(isDiff).length : 0;

  const handleSave = async () => {
    if (!plantilla || !renovacion) return;

    setSaving(true);
    try {
      // Convert estado to tiene_cobertura for backend compatibility
      const itemsForBackend = itemsRespuesta.map(item => ({
        ...item,
        tiene_cobertura: item.estado === "si",
        no_aplica: item.estado === "no_aplica"
      }));

      // Evento: guardado del checklist
      logEvento("guardado", `El usuario guardó el checklist con ${porcentaje}% de cobertura.`);

      // Trazabilidad: comparación vs cotizaciones y ajustes solicitados
      if (comparisonMode) {
        const difs = itemsRespuesta.filter(isDiff);
        logEvento("comparacion_cotizaciones", difs.length
          ? `Comparación vs checklist de cotizaciones: ${difs.length} diferencia(s) detectada(s): ${difs.slice(0, 15).map(d => d.texto).join("; ")}`
          : "Comparación vs checklist de cotizaciones: sin diferencias.");
      }
      const ajustes = itemsRespuesta.filter(i => (i.ajuste_solicitado || "").trim());
      if (ajustes.length) {
        logEvento("ajustes_solicitados", `Ajustes a solicitar a la aseguradora en ${ajustes.length} cobertura(s): ${ajustes.slice(0, 15).map(a => `${a.texto}: ${a.ajuste_solicitado}`).join("; ")}`);
      }

      const payload = {
        renovacion_id: renovacion.id,
        plantilla_id: plantilla.id,
        realizado_por: (realizadoPor || "").trim() || undefined,
        supervisado_por: (supervisadoPor || "").trim() || undefined,
        items: itemsForBackend,
        observaciones_generales: (observaciones || "").trim() || undefined,
        tipo_cobertura_seleccionado: tipoCobertura || undefined,  // Send the selected coverage type
        started_at: startedAt ? startedAt.toISOString() : undefined,  // For duration tracking (admin/supervisor only)
        trazabilidad: eventosRef.current,  // Auditoría de la sesión
        origen: origen || undefined
      };

      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/completar`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Checklist guardado - ${result.porcentaje_cobertura}% de cobertura`);
        onSaved && onSaved(result);
        // Si la cobertura no es 100%, preguntar si desea crear un ticket en Mesa de Ayuda
        if ((result.porcentaje_cobertura ?? 0) < 100 && result.id && !result.ticket_numero) {
          setTicketPrompt({ id: result.id, porcentaje: result.porcentaje_cobertura });
        } else {
          handleClose();
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al guardar checklist");
      }
    } catch (error) {
      console.error("Error saving checklist:", error);
      toast.error("Error al guardar checklist");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setPlantilla(null);
    setItemsRespuesta([]);
    setRealizadoPor("");
    setSupervisadoPor("");
    setObservaciones("");
    setNoTemplate(false);
    setTipoCobertura("");
    setNeedsTipoCobertura(false);
    setTipoCoberturaOpciones([]);
    setTipoCoberturaLabel("Tipo de Cobertura");
    setSearchCobertura("");
    setStartedAt(null);
    setElapsedSec(0);
    setCotChecklist(null);
    eventosRef.current = [];
    setTicketPrompt(null);
    setCreandoTicket(false);
    onClose();
  };

  // Decisión del usuario sobre crear el ticket en Mesa de Ayuda (cobertura < 100%)
  const handleDecisionTicket = async (crear) => {
    if (!ticketPrompt?.id) { handleClose(); return; }
    setCreandoTicket(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${ticketPrompt.id}/decision-ticket`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ crear })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (crear && data.ticket_numero) {
          toast.success(`Ticket #${data.ticket_numero} creado en Mesa de Ayuda`);
          onSaved && onSaved({ id: ticketPrompt.id });
        } else if (crear) {
          toast.error(data.detail || "No se pudo crear el ticket");
        }
      } else {
        toast.error(data.detail || "Error al procesar la solicitud");
      }
    } catch (e) {
      toast.error("Error al procesar la solicitud");
    } finally {
      setCreandoTicket(false);
      handleClose();
    }
  };

  // Group items by category, filtered by search
  const filteredItems = searchCobertura
    ? itemsRespuesta.filter(item =>
        item.texto?.toLowerCase().includes(searchCobertura.toLowerCase()) ||
        (item.categoria || "").toLowerCase().includes(searchCobertura.toLowerCase())
      )
    : itemsRespuesta;

  const groupedItems = filteredItems.reduce((acc, item) => {
    const cat = item.categoria || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Stats - excluir N/A del cálculo
  const itemsAplicables = itemsRespuesta.filter(i => i.estado !== "no_aplica");
  const totalItems = itemsAplicables.length;
  const itemsConCobertura = itemsAplicables.filter(i => i.estado === "si").length;
  const itemsNoAplica = itemsRespuesta.filter(i => i.estado === "no_aplica").length;
  const porcentaje = totalItems > 0 ? Math.round((itemsConCobertura / totalItems) * 100) : 0;

  // AI Document Analysis
  const handleAnalyzeDocument = async (file) => {
    if (!file || itemsRespuesta.length === 0) return;
    
    const validTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato no soportado. Use PDF, PNG, JPG o WEBP.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("El archivo es muy grande (máx 20MB).");
      return;
    }
    
    setAnalyzing(true);
    setAiResult(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("items_json", JSON.stringify(itemsRespuesta.map(i => ({
        item_id: i.item_id,
        texto: i.texto,
        monto_asegurado: i.monto_asegurado || "",
        ajuste_solicitado: i.ajuste_solicitado || ""
      }))));
      
      const headers = getAuthHeaders().headers || {};
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/analizar-documento-ia`, {
        method: "POST",
        headers: { Authorization: headers.Authorization || headers.authorization },
        body: formData
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al analizar documento");
      }
      
      const data = await res.json();
      
      if (data.error) {
        toast.error(data.error);
        return;
      }
      
      // Auto-mark found items as SI
      const foundIds = new Set(data.items_encontrados || []);
      const montos = data.montos_por_item || {};
      const ajustesIA = data.ajustes_por_item || {};
      setItemsRespuesta(prev => prev.map(item => ({
        ...item,
        estado: foundIds.has(item.item_id) ? "si" : item.estado,
        monto_asegurado: montos[item.item_id] ? String(montos[item.item_id]) : item.monto_asegurado,
        ajuste_solicitado: (!item.ajuste_solicitado && ajustesIA[item.item_id]?.sugerencia)
          ? ajustesIA[item.item_id].sugerencia
          : item.ajuste_solicitado
      })));
      
      setAiResult(data);
      const nMontos = Object.keys(montos).length;
      const nAjustes = Object.keys(ajustesIA).length;
      logEvento("documento_ia", `El usuario cargó el documento "${file.name}" y la IA detectó ${data.total_encontrados || 0} cobertura(s) en el documento${nMontos ? ` y ${nMontos} monto(s) asegurado(s)/sublímite(s)` : ""}.`);
      if (nAjustes) {
        logEvento("ajustes_sugeridos_ia", `La IA comparó los montos del documento contra lo registrado/solicitado y sugirió ${nAjustes} ajuste(s): ${Object.values(ajustesIA).slice(0, 10).map(a => `${a.cobertura}: ${a.sugerencia}`).join("; ")}`);
      }
      toast.success(`IA encontró ${data.total_encontrados} coberturas en el documento`);
      
    } catch (error) {
      console.error("Error analyzing document:", error);
      toast.error(error.message || "Error al analizar el documento");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleAnalyzeDocument(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) handleAnalyzeDocument(file);
    e.target.value = "";
  };

  return (
    <>
    {open && <ClienteAtencionWatcher nombre={renovacion?.cliente} />}
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 justify-between">
            <span className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Checklist de Evaluación de Póliza
            </span>
            {isAdminOrSupervisor && startedAt && (
              <span
                className="flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 select-none"
                title="Tiempo transcurrido desde que abriste el checklist (visible solo para admin/supervisor)"
                data-testid="checklist-timer"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                {formatElapsed(elapsedSec)}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {renovacion?.cliente} - {renovacion?.ramo}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-slate-500">Cargando plantilla...</p>
          </div>
        ) : noTemplate ? (
          <div className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No hay plantilla disponible</h3>
            <p className="text-slate-500 mt-2">
              No existe una plantilla de checklist para el ramo <strong>"{renovacion?.ramo}"</strong>.
            </p>
            <p className="text-slate-400 text-sm mt-1">
              Un administrador debe crear la plantilla primero desde la pestaña "Plantillas".
            </p>
            <Button variant="outline" className="mt-4" onClick={handleClose}>
              Cerrar
            </Button>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Client Info Header */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div>
                <Label className="text-xs text-slate-500">Cliente</Label>
                <p className="font-medium text-sm">{renovacion?.cliente}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Póliza</Label>
                <p className="font-medium text-sm">{renovacion?.poliza}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Ramo</Label>
                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                  {renovacion?.ramo}
                </Badge>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Compañía</Label>
                <p className="font-medium text-sm">{renovacion?.compania || "-"}</p>
              </div>
            </div>

            {/* Tipo de Cobertura/Formato Selector (para VEHICULO DE MOTOR y FIDELIDAD 3D) */}
            {needsTipoCobertura && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg">
                <Label className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2 block">
                  Seleccione el {tipoCoberturaLabel} *
                </Label>
                <Select value={tipoCobertura} onValueChange={handleTipoCoberturaChange}>
                  <SelectTrigger className="w-full md:w-72 bg-white">
                    <SelectValue placeholder={`Seleccionar ${tipoCoberturaLabel.toLowerCase()}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {tipoCoberturaOpciones.map((opcion) => (
                      <SelectItem key={opcion} value={opcion}>{opcion}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!tipoCobertura && (
                  <p className="text-xs text-blue-600 mt-2">
                    Debe seleccionar un {tipoCoberturaLabel.toLowerCase()} para cargar los items del checklist
                  </p>
                )}
              </div>
            )}

            {/* Evaluator Info - Auto-filled */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Realizado Por
                </Label>
                <Input
                  value={realizadoPor}
                  onChange={(e) => setRealizadoPor(e.target.value)}
                  placeholder="Nombre del ejecutivo..."
                  className="bg-slate-50"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Supervisado Por
                </Label>
                <Input
                  value={supervisadoPor}
                  onChange={(e) => setSupervisadoPor(e.target.value)}
                  placeholder="Nombre del supervisor..."
                  className="bg-slate-50"
                />
              </div>
            </div>

            {/* AI Document Analysis - Drag & Drop */}
            {(!needsTipoCobertura || tipoCobertura) && itemsRespuesta.length > 0 && (
              <div className="relative">
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-all ${
                    dragOver 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-slate-300 hover:border-blue-400 bg-slate-50/50 dark:bg-slate-800/30'
                  } ${analyzing ? 'pointer-events-none opacity-70' : 'cursor-pointer'}`}
                  onClick={() => !analyzing && document.getElementById('ai-doc-upload')?.click()}
                >
                  <input
                    id="ai-doc-upload"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    className="hidden"
                    onChange={handleFileInput}
                    disabled={analyzing}
                  />
                  {analyzing ? (
                    <div className="flex flex-col items-center gap-2 py-2">
                      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                      <p className="text-sm font-medium text-blue-600">Analizando documento con IA...</p>
                      <p className="text-xs text-slate-500">Esto puede tomar unos segundos</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4 py-1">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                        <Sparkles className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left flex-1">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Analizar documento con IA</p>
                        <p className="text-xs text-slate-500">Arrastra un PDF o imagen aquí, o haz click para seleccionar</p>
                      </div>
                      <FileUp className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                </div>

                {/* AI Results */}
                {aiResult && !analyzing && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg space-y-3" data-testid="ai-result-panel">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200 flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4" />
                        Resultado del análisis
                      </p>
                      <button onClick={() => setAiResult(null)} className="text-slate-400 hover:text-slate-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* 3 KPI cards */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900 p-2">
                        <div className="flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-semibold uppercase tracking-wide">Coinciden</span>
                        </div>
                        <div className="text-xl font-bold text-emerald-800 dark:text-emerald-200" data-testid="ai-count-matches">
                          {aiResult.total_encontrados || 0}
                        </div>
                      </div>
                      <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-2">
                        <div className="flex items-center gap-1 text-red-700 dark:text-red-300">
                          <XCircle className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-semibold uppercase tracking-wide">Faltan en doc.</span>
                        </div>
                        <div className="text-xl font-bold text-red-800 dark:text-red-200" data-testid="ai-count-missing">
                          {aiResult.total_no_encontrados || 0}
                        </div>
                      </div>
                      <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-2">
                        <div className="flex items-center gap-1 text-amber-700 dark:text-amber-300">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-semibold uppercase tracking-wide">Extra en doc.</span>
                        </div>
                        <div className="text-xl font-bold text-amber-800 dark:text-amber-200" data-testid="ai-count-extra">
                          {aiResult.coberturas_adicionales?.length || 0}
                        </div>
                      </div>
                    </div>

                    {/* Coberturas que coinciden (en checklist Y en documento) */}
                    {aiResult.items_encontrados?.length > 0 && (
                      <div className="rounded-md border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-900 p-2">
                        <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 mb-1.5 flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Coberturas que coinciden ({aiResult.items_encontrados.length})
                        </p>
                        <div className="flex flex-wrap gap-1" data-testid="ai-list-matches">
                          {aiResult.items_encontrados.map((id) => {
                            const it = itemsRespuesta.find((x) => x.item_id === id);
                            const label = it?.texto || id;
                            return (
                              <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-200">
                                <CheckCircle2 className="w-3 h-3" />
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Coberturas del checklist que NO están en el documento */}
                    {aiResult.items_no_encontrados?.length > 0 && (
                      <div className="rounded-md border border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900 p-2">
                        <p className="text-xs font-semibold text-red-800 dark:text-red-300 mb-1.5 flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5" />
                          En checklist pero NO en el documento ({aiResult.items_no_encontrados.length})
                        </p>
                        <div className="flex flex-wrap gap-1" data-testid="ai-list-missing">
                          {aiResult.items_no_encontrados.map((item, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-white dark:bg-slate-900 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-200">
                              <XCircle className="w-3 h-3" />
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Coberturas en el documento que NO están en el checklist */}
                    {aiResult.coberturas_adicionales?.length > 0 && (
                      <div className="rounded-md border border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-900 p-2">
                        <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1.5 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          En el documento pero NO en el checklist ({aiResult.coberturas_adicionales.length})
                        </p>
                        <p className="text-[10px] text-amber-700 dark:text-amber-400 italic mb-1.5">
                          Cobertura presente en la póliza pero no contemplada en tu plantilla.
                          Considera agregarla si aplica.
                        </p>
                        <div className="flex flex-wrap gap-1" data-testid="ai-list-extra">
                          {aiResult.coberturas_adicionales.map((item, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-200">
                              <AlertTriangle className="w-3 h-3" />
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Ajustes sugeridos por la IA (monto leído vs lo registrado/solicitado) */}
                    {aiResult.ajustes_por_item && Object.keys(aiResult.ajustes_por_item).length > 0 && (
                      <div className="rounded-md border border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-900 p-2">
                        <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1.5 flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5" />
                          Ajustes sugeridos por la IA ({Object.keys(aiResult.ajustes_por_item).length})
                        </p>
                        <p className="text-[10px] text-blue-700 dark:text-blue-400 italic mb-1.5">
                          La IA comparó el monto del documento contra lo registrado/solicitado. Las sugerencias se prellenaron en "Ajuste a solicitar" si el campo estaba vacío.
                        </p>
                        <div className="space-y-1" data-testid="ai-list-ajustes">
                          {Object.values(aiResult.ajustes_por_item).map((a, i) => (
                            <div key={i} className="text-[11px] bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-900 rounded px-2 py-1 text-blue-900 dark:text-blue-200">
                              <span className="font-semibold">{a.cobertura}:</span> {a.sugerencia}
                              {(a.monto_documento || a.monto_previo) && (
                                <span className="text-slate-500"> (documento: {a.monto_documento || "N/D"} · registrado/solicitado: {a.monto_previo || "N/D"})</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {(!needsTipoCobertura || tipoCobertura) && (
            <div className="p-4 bg-white dark:bg-slate-900 border rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">{crEsPersonas() ? "Progreso de Validación" : "Progreso de Cobertura"}</span>
                <span className={`text-lg font-bold ${
                  porcentaje >= 80 ? 'text-green-600' : 
                  porcentaje >= 50 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {porcentaje}%
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-300 ${
                    porcentaje >= 80 ? 'bg-green-500' : 
                    porcentaje >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${porcentaje}%` }}
                />
              </div>
              <div className="flex gap-4 mt-2 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                  {itemsConCobertura} con cobertura
                </span>
                <span className="flex items-center gap-1">
                  <XCircle className="w-3 h-3 text-red-500" />
                  {totalItems - itemsConCobertura} sin cobertura
                </span>
                {itemsNoAplica > 0 && (
                  <span className="flex items-center gap-1">
                    <MinusCircle className="w-3 h-3 text-slate-400" />
                    {itemsNoAplica} N/A
                  </span>
                )}
              </div>
            </div>
            )}

            {/* Checklist Items by Category - Only show when items are loaded */}
            {(!needsTipoCobertura || tipoCobertura) && (
            <div className="space-y-4">
              {comparisonMode && (
                <div
                  className={`p-3 rounded-lg border flex items-center gap-2 ${diffCount > 0 ? "bg-red-50 border-red-300" : "bg-emerald-50 border-emerald-300"}`}
                  data-testid="checklist-comparacion-banner"
                >
                  {diffCount > 0 ? (
                    <>
                      <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                      <p className="text-sm font-semibold text-red-700">
                        HAY DIFERENCIAS: {diffCount} cobertura(s) difieren del checklist de cotizaciones
                      </p>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <p className="text-sm font-semibold text-emerald-700">
                        Sin diferencias con el checklist de cotizaciones
                      </p>
                    </>
                  )}
                  <span className="ml-auto text-[11px] text-slate-500">
                    Cotizaciones: {cotChecklist?.fecha || "s/f"} · {cotChecklist?.realizado_por || "N/D"}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  Items de Evaluación ({plantilla?.nombre})
                  {tipoCobertura && (
                    <Badge className="ml-2 bg-purple-100 text-purple-700">{tipoCobertura}</Badge>
                  )}
                </h3>
                <div className="relative w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchCobertura}
                    onChange={(e) => setSearchCobertura(e.target.value)}
                    placeholder="Buscar cobertura..."
                    className="pl-9 h-9 text-sm"
                    data-testid="search-cobertura-input"
                  />
                  {searchCobertura && (
                    <button onClick={() => setSearchCobertura("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {searchCobertura && (
                <p className="text-xs text-slate-500">Mostrando {filteredItems.length} de {itemsRespuesta.length} items</p>
              )}
              
              {Object.entries(groupedItems).map(([categoria, items]) => (
                <div key={categoria} className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 dark:bg-slate-800 px-4 py-2 font-medium text-sm">
                    {categoria}
                  </div>
                  <div className="divide-y">
                    {items.map((item, idx) => (
                      <div 
                        key={item.item_id} 
                        className={`p-3 ${
                          item.estado === "si" ? 'bg-green-50 dark:bg-green-900/20' : 
                          item.estado === "no_aplica" ? 'bg-slate-100 dark:bg-slate-800/50' :
                          'bg-white dark:bg-slate-900'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Columna 1 (solo lectura): respuesta del checklist de cotizaciones */}
                          {comparisonMode && (() => {
                            const [txt, cls] = COT_CHIP[item.cotizacion_estado] || ["—", "bg-slate-50 text-slate-400 border-slate-200"];
                            return (
                              <div className="flex flex-col items-center mt-1 shrink-0" data-testid={`cot-estado-${item.item_id}`} title="Respuesta del checklist de cotizaciones (solo lectura)">
                                <span className="text-[9px] text-slate-400 uppercase font-semibold">Cotiz.</span>
                                <span className={`w-10 h-8 rounded border flex items-center justify-center text-xs font-bold ${cls}`}>{txt}</span>
                              </div>
                            );
                          })()}
                          {/* Estado buttons - SI / NO / N/A */}
                          <div className="flex gap-1 mt-1">
                            <button
                              onClick={() => handleItemEstadoChange(item.item_id, item.estado === "si" ? "pendiente" : "si")}
                              data-testid={`item-si-${item.item_id}`}
                              className={`w-10 h-8 rounded flex items-center justify-center text-xs font-bold transition-colors ${
                                item.estado === "si" 
                                  ? 'bg-green-500 text-white' 
                                  : 'bg-slate-200 text-slate-600 hover:bg-green-100'
                              }`}
                            >
                              SI
                            </button>
                            <button
                              onClick={() => handleItemEstadoChange(item.item_id, item.estado === "no_aplica" ? "pendiente" : "no_aplica")}
                              data-testid={`item-na-${item.item_id}`}
                              className={`w-10 h-8 rounded flex items-center justify-center text-xs font-bold transition-colors ${
                                item.estado === "no_aplica" 
                                  ? 'bg-slate-500 text-white' 
                                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                              }`}
                              title="No Aplica - no se contabiliza en el progreso"
                            >
                              N/A
                            </button>
                          </div>
                          
                          {/* Item content */}
                          <div className="flex-1">
                            <p className={`font-medium text-sm ${
                              item.estado === "no_aplica" 
                                ? 'text-slate-400 line-through' 
                                : 'text-slate-900 dark:text-white'
                            }`}>
                              {idx + 1}. {item.texto}
                              {item.observacion && (
                                <Badge
                                  variant="outline"
                                  className="ml-2 bg-amber-100 text-amber-800 border-amber-300 text-xs gap-1 align-middle"
                                  title={item.observacion}
                                  data-testid={`modal-item-observacion-${item.item_id}`}
                                >
                                  <MessageSquare className="w-3 h-3" /> {item.observacion}
                                </Badge>
                              )}
                            </p>
                            
                            {/* Comments - only show if not N/A */}
                            {item.estado !== "no_aplica" && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              <div>
                                <Label className="text-xs text-slate-500">Comentario Ejecutivo</Label>
                                <Input
                                  value={item.comentario_ejecutivo}
                                  onChange={(e) => handleComentarioEjecutivo(item.item_id, e.target.value)}
                                  placeholder="Agregar comentario..."
                                  className="text-sm h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Comentario Supervisor</Label>
                                <Input
                                  value={item.comentario_supervisor}
                                  onChange={(e) => handleComentarioSupervisor(item.item_id, e.target.value)}
                                  placeholder="Agregar comentario..."
                                  className="text-sm h-8"
                                />
                              </div>
                            </div>
                            )}

                            {/* Columna 3: diferencias vs cotizaciones (registros transferidos) */}
                            {comparisonMode && (
                              <div className="mt-2">
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs text-slate-500">Diferencia encontrada</Label>
                                  {isDiff(item) && (
                                    <Badge className="bg-red-100 text-red-700 border border-red-300 text-[10px] px-1.5 py-0" data-testid={`dif-badge-${item.item_id}`}>
                                      HAY DIFERENCIAS
                                    </Badge>
                                  )}
                                </div>
                                <Input
                                  value={item.diferencia || ""}
                                  onChange={(e) => handleItemField(item.item_id, "diferencia", e.target.value)}
                                  placeholder="Detalle de la diferencia a reportar a la aseguradora..."
                                  className={`text-sm h-8 mt-0.5 ${isDiff(item) ? "border-red-300 bg-red-50/50" : ""}`}
                                  data-testid={`dif-input-${item.item_id}`}
                                />
                              </div>
                            )}

                            {/* Monto asegurado + ajuste a solicitar (renovación normal) */}
                            {showMontos && item.estado !== "no_aplica" && (
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                <div>
                                  <Label className="text-xs text-slate-500">Monto asegurado / sublímite</Label>
                                  <Input
                                    value={item.monto_asegurado || ""}
                                    onChange={(e) => handleItemField(item.item_id, "monto_asegurado", e.target.value)}
                                    onBlur={() => handleItemField(item.item_id, "monto_asegurado", formatNumericValue(item.monto_asegurado))}
                                    placeholder="Ej: 5,000.00"
                                    className="text-sm h-8"
                                    data-testid={`monto-input-${item.item_id}`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-slate-500">Ajuste a solicitar a la aseguradora</Label>
                                  <Input
                                    value={item.ajuste_solicitado || ""}
                                    onChange={(e) => handleItemField(item.item_id, "ajuste_solicitado", e.target.value)}
                                    onBlur={() => handleItemField(item.item_id, "ajuste_solicitado", formatNumericValue(item.ajuste_solicitado))}
                                    placeholder="Ej: aumentar sublímite a 10,000"
                                    className="text-sm h-8"
                                    data-testid={`ajuste-input-${item.item_id}`}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            )}

            {/* Observations - Only show when items are loaded */}
            {(!needsTipoCobertura || tipoCobertura) && (
            <div>
              <Label>Observaciones Generales</Label>
              <Textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Observaciones adicionales sobre la evaluación..."
                rows={3}
              />
            </div>
            )}
          </div>
        )}

        {!loading && !noTemplate && (
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              data-testid="checklist-save-btn"
              disabled={saving || (needsTipoCobertura && !tipoCobertura)}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Checklist
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>

    {/* Prompt: crear ticket en Mesa de Ayuda cuando cobertura < 100% */}
    <Dialog open={!!ticketPrompt} onOpenChange={(v) => { if (!v && !creandoTicket) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="checklist-ticket-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Cobertura incompleta
          </DialogTitle>
          <DialogDescription>
            El checklist se guardó con <strong>{ticketPrompt?.porcentaje}%</strong> de cobertura.
            ¿Desea crear un ticket en Mesa de Ayuda para gestionar con la aseguradora las coberturas faltantes?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => handleDecisionTicket(false)}
            disabled={creandoTicket}
            data-testid="checklist-ticket-no"
          >
            No, gracias
          </Button>
          <Button
            onClick={() => handleDecisionTicket(true)}
            disabled={creandoTicket}
            className="bg-[#193966] hover:bg-[#142d50] text-white"
            data-testid="checklist-ticket-si"
          >
            {creandoTicket ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando ticket...</>
            ) : (
              <>Sí, crear ticket</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
